<script setup lang="ts">
import { sbProps } from "@js/util/sbx/pageUtils";
import { getUserPlatform, isPWA, Platform } from "@js/util/browserUtils";
import { usePwaStore } from "./store/pwaStore";
import PwaMenuInstallModalIos from "./pwa-menu-install-modal-ios.vue";

interface Props {
    // if this component is located in the sidebar or the notifications setting page
    page: "sidebar" | "settings";
    // the digistorm prompt for google/apple, if it exists
    installApple: string | null;
    installGoogle: string | null;
    // whether the PWA app can be installed
    pwaEnabled: boolean;
}

const props = defineProps<Props>();
const pwaStore = usePwaStore();

// The type of installation prompt to show
const pwaPromptType = window.Vue.computed(() => {
    // - If on iOS and the digistorm app exists
    if (props.installApple && getUserPlatform() === Platform.iOS) {
        return { id: "iOS-digistorm", url: props.installApple };
    }

    // - If on Android and the digistorm app exists
    if (props.installGoogle && getUserPlatform() === Platform.Android) {
        return { id: "Android-digistorm", url: props.installGoogle };
    }

    // - If PWA is enabled, we are outside of it, and the above are not true
    if (props.pwaEnabled && !isPWA()) {
        /**
         * We can't use beforeinstallprompt on iOS, so we'll show our own prompt and
         * guide the user with instructions on how to add to home screen instead...
         */
        if (getUserPlatform() === Platform.iOS) {
            return { id: "iOS", url: null };
        }

        /**
         * Otherwise, rely on beforeinstallprompt to detect browser compatibility before showing the install button
         * This event is not widely supported, so we either need to provide a manual guide (e.g. iOS, see above)
         * or wait for the event to tell us that the browser is capable of installing the PWA.
         *
         * If we don't do this, the install button will end up showing on browsers that don't support it, and do nothing upon click...
         */
        if (pwaStore.deferredPrompt !== null) {
            return { id: "Browser", url: null };
        }
    }

    return { id: null, url: null };
});

/**
 * When the user clicks "Install" on the PWA prompt
 */
async function onInstallPwa(): Promise<void> {
    // If showing the iOS prompt, show the modal
    if (pwaPromptType.value.id === "iOS") {
        pwaStore.isPwaIOSModalOpen = true;
        return;
    }

    // Otherwise, trigger the browser install
    await pwaStore.onInstallPwa();
}

/**
 * To tell the parent whether we are showing any content or not
 */
const hasVisibleContent = window.Vue.computed(() => {
    return pwaPromptType.value.id !== null;
});

defineExpose({ hasVisibleContent });
</script>
<template>
    <p v-if="pwaPromptType.id !== null">
        To receive push notifications and enjoy a better experience, install the {{ sbProps.siteTitle }} web app on your
        device.
    </p>

    <a v-if="pwaPromptType.url" class="button install-button" :href="pwaPromptType.url">Install</a>
    <a v-else-if="pwaPromptType.id !== null" class="button install-button" href="#" @click="onInstallPwa">Install</a>

    <pwa-menu-install-modal-ios />
</template>
<style scoped></style>
